import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Package, Loader2 } from 'lucide-react';
import { formatCurrency, getCountryFlag } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CreateAcquisitionModal({ open, onClose, book, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_url: '',
    purchase_price: book?.used_price?.toString() || '',
    condition_note: '',
    ship_to: 'prep_center',
    supplier_country: book?.seller_country || 'GB',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!book) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/acquisitions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          book_id: book.book_id,
          supplier_name: formData.supplier_name,
          supplier_url: formData.supplier_url || null,
          purchase_price: parseFloat(formData.purchase_price),
          condition_note: formData.condition_note || null,
          ship_to: formData.ship_to,
          supplier_country: formData.supplier_country,
        }),
      });

      if (response.ok) {
        onSuccess();
        onClose();
        setFormData({
          supplier_name: '',
          supplier_url: '',
          purchase_price: '',
          condition_note: '',
          ship_to: 'prep_center',
          supplier_country: 'GB',
        });
      }
    } catch (error) {
      console.error('Failed to create acquisition:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!book) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="acquisition-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-gold" />
            Create Acquisition
          </DialogTitle>
        </DialogHeader>

        {/* Book Info */}
        <div className="p-3 bg-muted/50 rounded-lg border mb-4">
          <div className="font-medium">{book.title}</div>
          <div className="text-sm text-muted-foreground mt-1">
            ISBN: {book.isbn} • Amazon Price: {formatCurrency(book.amazon_price)}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="supplier_name">Supplier Name *</Label>
              <Input
                id="supplier_name"
                placeholder="e.g., World of Books"
                value={formData.supplier_name}
                onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                required
                data-testid="input-supplier-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="purchase_price">Purchase Price (£) *</Label>
              <Input
                id="purchase_price"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.purchase_price}
                onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                required
                data-testid="input-purchase-price"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplier_url">Supplier URL</Label>
            <Input
              id="supplier_url"
              type="url"
              placeholder="https://..."
              value={formData.supplier_url}
              onChange={(e) => setFormData({ ...formData, supplier_url: e.target.value })}
              data-testid="input-supplier-url"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="supplier_country">Supplier Country</Label>
              <Select
                value={formData.supplier_country}
                onValueChange={(value) => setFormData({ ...formData, supplier_country: value })}
              >
                <SelectTrigger data-testid="select-supplier-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GB">{getCountryFlag('GB')} United Kingdom</SelectItem>
                  <SelectItem value="US">{getCountryFlag('US')} United States</SelectItem>
                  <SelectItem value="DE">{getCountryFlag('DE')} Germany</SelectItem>
                  <SelectItem value="FR">{getCountryFlag('FR')} France</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ship_to">Ship To</Label>
              <Select
                value={formData.ship_to}
                onValueChange={(value) => setFormData({ ...formData, ship_to: value })}
              >
                <SelectTrigger data-testid="select-ship-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prep_center">Prep Center (Default)</SelectItem>
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="condition_note">Condition Notes</Label>
            <Textarea
              id="condition_note"
              placeholder="Any notes about the book condition..."
              value={formData.condition_note}
              onChange={(e) => setFormData({ ...formData, condition_note: e.target.value })}
              className="resize-none"
              rows={2}
              data-testid="input-condition-note"
            />
          </div>

          {/* Import Warning */}
          {formData.supplier_country !== 'GB' && (
            <div className="p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg text-sm text-orange-600 dark:text-orange-400">
              <strong>Import Notice:</strong> This purchase is from outside the UK. 
              Import VAT (20%) and customs duties may apply, affecting your profit margin.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="btn-gold" data-testid="submit-acquisition">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Acquisition
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
